import React, { useEffect, useMemo, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { resolveImageEntries } from '../../utils/entityImages';
import { monsterKind } from '../../utils/monsterKind';
import { npcSheets, NPC_SHEET_SIZE, npcRectInBounds } from '../../data/guide/npcPhotoSheets';
import { npcs } from '../../data/guide/npcs';
import type { WorldData } from '../../types/world';
type SheetState = {
  status: 'cargando' | 'ok' | 'error';
  w?: number;
  h?: number;
};

/** Panel del staff: cifras reales del paquete VANGUARD_NPC y de la clasificación NPC, calculadas en este navegador. */
export function NpcPhotoPackCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    npcPack,
    byMonster
  } = useMemo(() => resolveImageEntries(w), [w]);
  const [run, setRun] = useState(false);
  const [sheets, setSheets] = useState<Record<number, SheetState>>({});
  useEffect(() => {
    if (!run) return;
    Object.entries(npcSheets).forEach(([id, url]) => {
      const n = Number(id);
      setSheets(s => ({
        ...s,
        [n]: {
          status: 'cargando'
        }
      }));
      const img = new Image();
      img.onload = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'ok',
          w: img.naturalWidth,
          h: img.naturalHeight
        }
      }));
      img.onerror = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'error'
        }
      }));
      img.src = url;
    });
  }, [run]);
  const nuevas = npcPack.filter(r => r.status === 'nueva');
  const conservadas = npcPack.filter(r => r.status === 'conservada');
  const fuera = npcPack.filter(r => r.status === 'fuera de catálogo');
  const badRect = npcPack.filter(r => !npcRectInBounds(r.photo));
  const loaded = Object.values(sheets).filter(s => s.status === 'ok').length;
  const failed = Object.entries(sheets).filter(([, s]) => s.status === 'error').map(([k]) => Number(k));
  const nameDiff = npcPack.filter(r => r.catalogName && r.catalogName.trim().toLowerCase() !== r.photo.sourceName.trim().toLowerCase());
  const allNpcs = Array.from(w.monsters.values()).filter(m => monsterKind(w, m) === 'npc');
  const staffNpcPhotos = npcs.filter(n => n.monsterId !== undefined && byMonster.get(n.monsterId)?.image?.origin === 'staff');
  const report = [`IDs del paquete VANGUARD_NPC: ${npcPack.length} · en 06_monsters: ${npcPack.length - fuera.length} · fuera del catálogo: ${fuera.length}`, `Fotos nuevas: ${nuevas.length} · conservadas (ya tenían foto): ${conservadas.length}${conservadas.length ? ` — ${conservadas.map(r => `${r.photo.id} ${r.catalogName}`).join(', ')}` : ''}`, `Fotos del staff de NPC de la guía, sin tocar: ${staffNpcPhotos.map(n => `${n.name} (${n.monsterId})`).join(', ')}`, `Láminas que cargan: ${run ? `${loaded}/3` : 'sin comprobar'} · recortes dentro de su celda: ${npcPack.length - badRect.length}/${npcPack.length}`, `Entidades clasificadas como NPC en el catálogo: ${allNpcs.length}`, '', ...fuera.map(r => `${r.photo.id}\t${r.photo.sourceName}\tno existe en 06_monsters: no se muestra`), ...badRect.map(r => `${r.photo.id}\t${r.photo.sourceName}\trecorte fuera de su celda`), ...failed.flatMap(s => npcPack.filter(r => r.photo.sheet === s).map(r => `${r.photo.id}\t${r.photo.sourceName}\tla lámina ${s} no cargó`)), ...Object.entries(sheets).filter(([, s]) => s.status === 'ok' && (s.w !== s.h || (s.w ?? 0) !== NPC_SHEET_SIZE)).map(([k, s]) => `Lámina ${k}\t${s.w}×${s.h}\tno mide 1536 × 1536: el recorte se escala proporcionalmente`), ...nameDiff.map(r => `${r.photo.id}\tlámina "${r.photo.sourceName}" · catálogo "${r.catalogName}"\tnombre distinto (se asocia por ID; revisar identidad)`)];
  return <section aria-labelledby="npc-pack" className="mt-8" data-id="element-1609" data-mp="3gc0qwcmix537">
      <h3 id="npc-pack" className="text-lg font-semibold text-white" data-id="element-1610" data-mp="3cd0at1vfkgjw">Paquete VANGUARD_NPC</h3>
      <p className="mt-1 text-sm text-gray-400" data-id="element-1611" data-mp="24z6pxh7xlyxx">Índice JSON no recibido: recortes medidos sobre las láminas; procedencia y retoque pendientes por foto.</p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-1612" data-mp="20z69jr19vdo8">
        <button type="button" onClick={() => setRun(true)} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1613" data-mp="3othtuzeb1us9">
          Comprobar carga de las láminas NPC
        </button>
        <CopyTextButton text={report.join('\n')} label="Copiar comprobación NPC" data-id="element-1614" data-mp="3juoh596ue7t5" />
      </div>
      <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-white/[0.03] p-3 text-xs text-gray-300" data-id="element-1615" data-mp="3jsbz6w133xg3">{report.join('\n')}</pre>
      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8" data-id="element-1616" data-mp="gcwumabh6g2c">
        {nuevas.map(r => <li key={r.photo.id} className="text-center" data-id="element-1617" data-mp="2dnefan9rlyd">
            <EntityImage entry={byMonster.get(r.photo.id)} alt={r.catalogName ?? r.photo.sourceName} name={r.catalogName ?? r.photo.sourceName} size="card" kind="persona" data-id="element-1618" data-mp="6fihajkum7e9" />
            <span className="mt-1 block truncate text-xs text-gray-400" data-id="element-1619" data-mp="2n6s9ittdumu2">{r.photo.id} · {r.catalogName}</span>
          </li>)}
      </ul>
    </section>;
}